import apiClient from "../helpers/apiClient";
import { format, isValid, parseISO } from "date-fns";

export const employeeCategoryService = {
  getEmployeeCategories,
  getEmployeeCategory,
  createEmployeeCategory,
  updateEmployeeCategory,
  deleteEmployeeCategory,
  setEmployeeCategoryStatus,
};

function displayValue(value) {
  return value === null || value === undefined || value === "" ? "-" : value;
}

function formatEmployeeCategoryDate(value) {
  if (value === null || value === undefined || value === "") return "-";

  const date =
    value instanceof Date
      ? value
      : typeof value === "string"
        ? parseISO(value)
        : new Date(value);

  return isValid(date) ? format(date, "dd MMM yyyy") : "-";
}

function findEmployeeCategoryPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const wrapped = payload.employee_category_info;
  if (Array.isArray(wrapped)) return wrapped;
  if (wrapped && typeof wrapped === "object") return [wrapped];
  if (payload.employee_category_id !== undefined) return [payload];
  return [];
}

function mapEmployeeCategory(raw = {}) {
  return {
    id: displayValue(raw.employee_category_id),
    name: displayValue(raw.employee_category_name),
    employeeTypeId: displayValue(raw.employee_type_id),
    employeeTypeName: displayValue(raw.employee_type_name),
    statusId: displayValue(raw.employee_category_status_id),
    statusName: displayValue(raw.employee_category_status_name),
    createdBy: displayValue(raw.encoded_by_name),
    createdAt: formatEmployeeCategoryDate(raw.encoded_date),
    createdAtRaw: raw.encoded_date ?? "",
    lastChangedBy: displayValue(raw.last_changed_by_name),
    lastChangedAt: formatEmployeeCategoryDate(raw.last_changed_date),
  };
}

function getErrorMessage(error) {
  return error.response?.data?.msg || "Something went wrong";
}

function extractWriteResult(response) {
  const data = response?.data;
  const messageObject = Array.isArray(data?.result_status)
    ? data.result_status[0]
    : Array.isArray(data)
      ? data[0]
      : data;

  return {
    message:
      messageObject?.message_text ||
      messageObject?.Message_Text ||
      "Saved successfully.",
    refNo: messageObject?.message_ref_no ?? messageObject?.Message_Ref_No ?? null,
  };
}

async function handleRequest(request) {
  try {
    return await request();
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

async function handleWriteRequest(request) {
  try {
    const response = await request();
    return extractWriteResult(response);
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

function getEmployeeCategories() {
  return handleRequest(async () => {
    const response = await apiClient.get("/hr/employee-categories");
    const data = response?.data;
    return {
      employeeCategories: findEmployeeCategoryPayload(data).map(mapEmployeeCategory),
      employeeTypeOptions: Array.isArray(data?.employee_type_info)
        ? data.employee_type_info
        : [],
    };
  });
}

function getEmployeeCategory(id) {
  return handleRequest(async () => {
    const response = await apiClient.get(`/hr/employee-categories/${id}`);
    const [employeeCategory] = findEmployeeCategoryPayload(response?.data);
    return employeeCategory ? mapEmployeeCategory(employeeCategory) : null;
  });
}

function createEmployeeCategory(name, employeeTypeId) {
  return handleWriteRequest(() =>
    apiClient.post("/hr/employee-categories", {
      employee_category_name: name,
      employee_type_id: Number(employeeTypeId),
    })
  );
}

function updateEmployeeCategory(id, name, statusId, employeeTypeId) {
  return handleWriteRequest(() =>
    apiClient.put(`/hr/employee-categories/${id}`, {
      employee_category_name: name,
      employee_category_status_id: String(statusId),
      employee_type_id: Number(employeeTypeId),
    })
  );
}

function deleteEmployeeCategory(id) {
  return handleWriteRequest(() => apiClient.delete(`/hr/employee-categories/${id}`));
}

function setEmployeeCategoryStatus(id, statusId) {
  return handleWriteRequest(() =>
    apiClient.patch(`/hr/employee-categories/${id}/status`, {
      employee_category_status_id: String(statusId),
    })
  );
}
